import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import {
  formatDate,
  getPackagesCommission,
  getUniqueOptions,
  price,
} from 'utils/helpers';
import { getCurrentBooking } from 'redux/selectors/current_booking';

const renderPassengerReport = (splits, passengerTypesArr, getPassengerAttr) => {
  const totalPassengersTypeCount = [];

  if (passengerTypesArr) {
    passengerTypesArr.forEach((type) => {
      const { id: passengerTypeID } = type;
      const passenger = { name: type.name, count: 0 };

      getPassengerAttr.forEach((passengerAttr) => {
        // check if booking passenger exists in reservation passenger list
        const checkPassengerBelongs = splits.some(
          (el) => el.consumer_id === passengerAttr.id
        );

        // exit if passenger doesn't belong to this reservation
        if (!checkPassengerBelongs) {
          return;
        }

        if (passengerAttr.passenger_type_id === passengerTypeID)
          passenger.count += 1;
      });

      if (passenger.count !== 0) totalPassengersTypeCount.push(passenger);
    });
  }

  return (
    !!totalPassengersTypeCount &&
    totalPassengersTypeCount.map((currentPassenger) => (
      <div
        key={currentPassenger.name}
        className="d-flex justify-content-between text-small"
      >
        <div className="left d-flex align-items-center">
          <i className="fas fa-user-friends me-2 text-primary" />
          <span className="card-body__count-name ps-2">
            {currentPassenger.count} {currentPassenger.name}
          </span>
        </div>
      </div>
    ))
  );
};

const Default = ({ experience }) => {
  const {
    selection_name,
    passenger_splits,
    sub_reservations_attributes,
    first_travel_date,
  } = experience;
  const booking = useSelector(getCurrentBooking);

  const getPassengerAttr = booking.passengers_attributes;

  const passengerTypes = useSelector((state) => state.types.passengerTypes);
  const [passengerTypesArr, setPassengerTypesArr] = useState([]);

  useEffect(() => {
    if (passengerTypes) setPassengerTypesArr(Object.values(passengerTypes));
  }, [passengerTypes]);

  // use in displaying ferry and accomodation options
  const uniqueOption = getUniqueOptions(sub_reservations_attributes);

  const formattedFirstTravelDate = formatDate(first_travel_date._value);
  const commissionPrice = getPackagesCommission([experience]);

  const customerFacingAdjustments = useMemo(() => {
    const collect = (reservation) => [
      ...(reservation.adjustments_attributes || []),
      ...(reservation.sub_reservations_attributes || []).flatMap(collect),
    ];
    const groups = {};
    collect(experience)
      .filter((adj) => adj.display_to_customer)
      .forEach((adj) => {
        const key = adj.adjustment_category_id;
        if (!groups[key]) {
          groups[key] = {
            category_id: key,
            display_name: adj.display_name,
            total: 0,
          };
        }
        groups[key].total += adj.gross_in_cents;
      });
    return Object.values(groups);
  }, [experience]);

  return (
    <div className="text-start">
      {selection_name && <div className="fw-bold mb-2">{selection_name}</div>}

      <div className="mb-2">{formattedFirstTravelDate}</div>

      {!isEmpty(uniqueOption) && (
        <div className="mb-1 d-flex flex-column justify-content-between text-small">
          {!isEmpty(uniqueOption) &&
            uniqueOption.map((option) => (
              <div key={option} className="left d-flex align-items-center">
                <i className="fas fa-check-circle me-2 text-primary" />
                <span className="card-body__count-name ps-2">
                  Includes {option}
                </span>
              </div>
            ))}
        </div>
      )}
      {renderPassengerReport(
        passenger_splits,
        passengerTypesArr,
        getPassengerAttr
      )}
      {!!commissionPrice && (
        <div className="d-flex justify-content-between text-small mt-2">
          <div>
            <div className="d-flex align-items-center">
              <i className="fas fa-usd-circle card-body__icon me-2" />
              <div className="ps-1">Commission</div>
            </div>
          </div>
          <div className="fw-bold">{price(commissionPrice)}</div>
        </div>
      )}
      {customerFacingAdjustments.map((group) => (
        <div
          className="d-flex justify-content-between text-small mt-2"
          key={group.category_id}
        >
          <div>
            <div className="d-flex align-items-center">
              <i className="fas fa-circle-info card-body__icon me-2" />
              <div className="ps-1">{group.display_name || 'Surcharge'}</div>
            </div>
          </div>
          <div className="fw-bold">{price(group.total)}</div>
        </div>
      ))}
    </div>
  );
};

Default.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  experience: PropTypes.object.isRequired,
};

export default Default;
